import { Controller } from "@hotwired/stimulus";
import TomSelect from "tom-select";

export default class extends Controller {
  static targets = [
    "breedSelect",
    "speciesRadio",
    "medicalConditions",
    "medicalConditionsDetailsLabel",
    "houseTrainedAdditionalDetails",
    "friendlyWithChildrenAdditionalDetails",
    "friendlyWithDogsAdditionalDetails",
    "friendlyWithCatsAdditionalDetails",
    "feedingScheduleAdditionalDetails",
    "pottyBreakAdditionalDetails",
    "timeAloneAdditionalDetails",
    "emptyMedicationsMsg",
    "medicationsList",
    "medicationTemplate",
    "medicationForm",
    "emergencyDirectiveAuthorization",
    "emergencyDirectiveFields",
    "emergencyDirectiveCprCopy",
    "emergencyDirectiveSignature",
    "emergencyDirectiveAttestation",
    "medicalRecordReleaseAuthorization",
    "medicalRecordReleaseFields"
  ]

  static values = {
    dogConditions: Array,
    catConditions: Array,
    dogConditionsWithDetails: Array,
    catConditionsWithDetails: Array
  }

  initialize() {
    // Combine dog and cat conditions with details into one array
    this.conditionsWithDetails = [
      ...this.dogConditionsWithDetailsValue,
      ...this.catConditionsWithDetailsValue
    ]
  }

  connect() {
    const species = this.speciesRadioTargets.find(radio => radio.checked)?.value
    if (species) this.filterBreedOptions(species)

    if (this.hasEmergencyDirectiveAuthorizationTarget) {
      this.updateEmergencyDirectiveState()
    }

    if (this.hasMedicalRecordReleaseAuthorizationTarget) {
      this.updateMedicalRecordReleaseState()
    }

    if (this.hasMedicalConditionsTarget) {
      this.medicalConditionsSelect = new TomSelect(this.medicalConditionsTarget, {
        create: true,
        persist: false,
        maxItems: 15,
        createOnBlur: true,
        plugins: {
          remove_button: {
            title: "Remove this condition",
          }
        },
        onChange: () => this.updateMedicalConditionDetailsLabel()
      });

      this.updateMedicalConditionDetailsLabel()
    }
  }

  speciesChanged(event) {
    this.filterBreedOptions(event.target.value)
    this.breedSelectTarget.value = ""
    if (this.hasMedicalConditionsTarget) {
      this.filterMedicalConditions(event.target.value)
    }
  }

  filterBreedOptions(species) {
    const breedSelect = this.breedSelectTarget

    const dogOptions = breedSelect.querySelectorAll('option.dog-breed')
    const catOptions = breedSelect.querySelectorAll('option.cat-breed')

    if (species === 'dog') {
      breedSelect.disabled = false
      dogOptions.forEach(opt => opt.hidden = false)
      catOptions.forEach(opt => opt.hidden = true)
    } else if (species === 'cat') {
      breedSelect.disabled = false
      catOptions.forEach(opt => opt.hidden = false)
      dogOptions.forEach(opt => opt.hidden = true)
    } else {
      // Disable breed select if no species is selected
      breedSelect.disabled = true
    }
  }

  filterMedicalConditions(species) {
    const select = this.medicalConditionsSelect

    // Clear previous selections
    select.clearOptions()
    select.clear()

    // Add new options dynamically
    let options = []
    if (species === "dog") {
      options = this.dogConditionsValue
    } else if (species === "cat") {
      options = this.catConditionsValue
    }

    options.forEach((label) => {
      select.addOption({ value: label, text: label })
    })

    // Refresh dropdown
    select.refreshOptions(false)
  }

  updateMedicalConditionDetailsLabel() {
    const selected = this.medicalConditionsSelect.getValue()
    const requiresDetails = selected.filter(v => this.conditionsWithDetails.includes(v))
    const labelEl = this.medicalConditionsDetailsLabelTarget

    if (requiresDetails.length > 0) {
      labelEl.textContent = `Additional details for: ${requiresDetails.join(", ")}`
    } else {
      labelEl.textContent = "Additional details (if needed for selected conditions)"
    }
  }

  showAdditionalDetails(event) {
    const targetName = event.params.targetName
    const target = this[`${targetName}Target`]
    if (target) {
      target.classList.remove("d-none")
    }
  }

  hideAdditionalDetails(event) {
    const targetName = event.params.targetName
    const target = this[`${targetName}Target`]
    if (target) {
      target.classList.add("d-none")
    }
  }

  medicalRecordReleaseAuthorizationChanged() {
    if (!this.medicalRecordReleaseAuthorizationTarget.checked) {
      this.medicalRecordReleaseFieldsTarget.querySelectorAll("input").forEach((input) => {
        if (input.type === "checkbox") {
          input.checked = false
        } else if (input.type !== "hidden") {
          input.value = ""
        }
      })
    }

    this.updateMedicalRecordReleaseState()
  }

  updateMedicalRecordReleaseState() {
    const authorization = this.medicalRecordReleaseAuthorizationTarget

    this.medicalRecordReleaseFieldsTarget.disabled = authorization.disabled || !authorization.checked
  }

  emergencyDirectiveAuthorizationChanged() {
    if (!this.emergencyDirectiveAuthorizationTarget.checked) {
      this.emergencyDirectiveFieldsTarget.querySelectorAll("input").forEach((input) => {
        if (input.type === "checkbox" || input.type === "radio") {
          input.checked = false
        } else if (input.type !== "hidden") {
          input.value = ""
        }
      })
    }

    this.updateEmergencyDirectiveState()
  }

  emergencyDirectiveActionChanged() {
    this.emergencyDirectiveSignatureTarget.value = ""
    this.emergencyDirectiveAttestationTarget.checked = false
    this.updateEmergencyDirectiveCprCopyState()
  }

  updateEmergencyDirectiveState() {
    const authorization = this.emergencyDirectiveAuthorizationTarget

    this.emergencyDirectiveFieldsTarget.disabled = authorization.disabled || !authorization.checked
    this.updateEmergencyDirectiveCprCopyState()
  }

  updateEmergencyDirectiveCprCopyState() {
    const dnrSelected = this.emergencyDirectiveFieldsTarget.querySelector('input[type="radio"][value="dnr"]').checked

    this.emergencyDirectiveCprCopyTarget.classList.toggle("text-muted", dnrSelected)
  }

  addMedication() {
    const template = this.medicationTemplateTarget
    const list = this.medicationsListTarget
    if (template && list) {
      // Clone the template content and append to the medications list
      const clone = template.content.cloneNode(true)
      list.appendChild(clone)
      if (this.hasEmptyMedicationsMsgTarget) {
        this.emptyMedicationsMsgTarget.classList.add("d-none")
      }
    }
  }

  removeMedication(event) {
    const btn = event.currentTarget
    const form = btn.closest('[data-pet-form-target="medicationForm"]')
    if (form) {
      form.remove()
      if (this.medicationFormTargets.length === 0) {
        this.emptyMedicationsMsgTarget.classList.remove("d-none")
      }
    }
  }
}
